<template>
  <v-container fluid class="px-12 pa-3">
    <v-row class="align-center mb-3">
      <v-col cols="12" class="d-flex justify-end pa-2 pt-6">
        <div class="d-flex gap-2">
          <v-btn
            color="primary"
            small
            depressed
            @click="performSync"
            :loading="syncing"
            :disabled="syncing"
            class="text-caption"
          >
            <v-icon small left>{{ syncing ? "mdi-loading mdi-spin" : "mdi-sync" }}</v-icon>
            {{ syncing ? "Syncing..." : "Sync" }}
          </v-btn>
          <v-btn 
            color="primary" 
            small 
            outlined 
            @click="navigateToHelpdesk"
            class="text-caption"
          >
            <v-icon small left>mdi-open-in-new</v-icon>
            Portal
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Compact Stats Row -->
    <v-row dense class="mb-3">
      <v-col cols="12" sm="6">
        <v-card 
          class="pa-3 rounded-lg" 
          :class="syncing ? 'disabled-state' : ''"
          flat
          outlined
        >
          <div class="d-flex align-center">
            <v-avatar color="blue lighten-5" size="40" class="mr-3">
              <v-icon color="blue" size="20">mdi-account-multiple</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-h6 font-weight-bold mb-0">{{ syncStats.users }}</div>
              <div class="text-caption text--secondary">Total Users</div>
            </div>
          </div>
          <v-progress-linear
            v-if="syncing"
            indeterminate
            color="blue"
            height="2"
            class="mt-2"
          />
        </v-card>
      </v-col>

      <v-col cols="12" sm="6">
        <v-card 
          class="pa-3 rounded-lg" 
          :class="syncing ? 'disabled-state' : ''"
          flat
          outlined
        >
          <div class="d-flex align-center">
            <v-avatar color="orange lighten-5" size="40" class="mr-3">
              <v-icon color="orange" size="20">mdi-school</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-h6 font-weight-bold mb-0">{{ syncStats.programmes }}</div>
              <div class="text-caption text--secondary">Programmes</div>
            </div>
          </div>
          <v-progress-linear
            v-if="syncing"
            indeterminate
            color="orange"
            height="2"
            class="mt-2"
          />
        </v-card>
      </v-col>
    </v-row>

    <!-- Compact Sync Status -->
    <v-row dense>
      <v-col cols="12">
        <v-card class="rounded-lg" flat outlined>
          <v-card-title class="py-2 px-3 d-flex align-center">
            <v-icon color="primary" size="20" class="mr-2">mdi-sync</v-icon>
            <span class="text-subtitle-2">Synchronization Status</span>
          </v-card-title>
          <v-divider></v-divider>
          
          <!-- Syncing Progress -->
          <v-card-text v-if="syncing" class="text-center py-4">
            <v-progress-circular
              indeterminate
              size="48"
              width="3"
              color="primary"
              class="mb-3"
            />
            <div class="text-subtitle-2 font-weight-medium mb-1">Synchronizing Data...</div>
            <div class="text-caption text--secondary">Syncing users and programmes</div>
          </v-card-text>

          <!-- Last Sync Summary -->
          <v-card-text v-else-if="lastSyncSummary" class="py-3">
            <div class="text-subtitle-2 font-weight-medium mb-2">Last Sync Summary</div>
            <v-row dense>
              <v-col cols="3">
                <div class="text-center pa-2">
                  <div class="text-h6 font-weight-bold success--text">
                    {{ lastSyncSummary.created }}
                  </div>
                  <div class="text-caption text--secondary">Created</div>
                </div>
              </v-col>
              <v-col cols="3">
                <div class="text-center pa-2">
                  <div class="text-h6 font-weight-bold info--text">
                    {{ lastSyncSummary.existing }}
                  </div>
                  <div class="text-caption text--secondary">Existing</div>
                </div>
              </v-col>
              <v-col cols="3">
                <div class="text-center pa-2">
                  <div 
                    class="text-h6 font-weight-bold"
                    :class="lastSyncSummary.failed > 0 ? 'error--text' : 'grey--text'"
                  >
                    {{ lastSyncSummary.failed }}
                  </div>
                  <div class="text-caption text--secondary">Failed</div>
                </div>
              </v-col>
              <v-col cols="3">
                <div class="text-center pa-2">
                  <div class="text-h6 font-weight-bold primary--text">
                    {{ lastSyncSummary.created + lastSyncSummary.existing + lastSyncSummary.failed }}
                  </div>
                  <div class="text-caption text--secondary">Total</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <!-- No Sync Yet -->
          <v-card-text v-else class="text-center py-5">
            <v-icon size="48" color="grey lighten-2" class="mb-2">mdi-database-sync</v-icon>
            <div class="text-subtitle-2 font-weight-medium mb-1">No Sync Performed Yet</div>
            <div class="text-caption text--secondary">Click "Sync" to synchronize data</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Compact Sync Dialog -->
    <v-dialog v-model="syncDialog" max-width="420" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="py-3 px-4 d-flex align-center">
          <v-icon color="primary" size="20" class="mr-2">mdi-sync-alert</v-icon>
          <span class="text-subtitle-1">Confirm Synchronization</span>
        </v-card-title>
        <v-divider></v-divider>
        
        <v-card-text class="py-3 px-4">
          <div class="text-body-2 mb-3">
            Are you sure you want to synchronize all data with the helpdesk system?
          </div>
          <v-alert type="info" dense outlined class="mb-0 text-caption">
            <div class="font-weight-medium mb-1">This will sync:</div>
            <ul class="pl-4 mb-0">
              <li>Users ({{ syncStats.users }})</li>
              <li>Institutions ({{ syncStats.institutions }})</li>
              <li>Programmes ({{ syncStats.programmes }})</li>
            </ul>
          </v-alert>
        </v-card-text>
        
        <v-divider></v-divider>
        <v-card-actions class="py-2 px-4">
          <v-spacer></v-spacer>
          <v-btn 
            text 
            small 
            @click="syncDialog = false" 
            :disabled="syncing"
            class="text-caption"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            small
            depressed
            @click="confirmSync"
            :loading="syncing"
            :disabled="syncing"
            class="text-caption"
          >
            <v-icon small left>mdi-check</v-icon>
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import env from "@/helpers/env";
import axios from "axios";

export default {
  name: "HelpdeskView",
  
  data: () => ({
    syncing: false,
    syncDialog: false,
    syncResult: null,
    syncStats: {
      users: 0,
      institutions: 1,
      programmes: 0,
    },
    lastSyncSummary: null,
    users: [],
    programmes: [],
  }),

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Helpdesk" }]);
    this.initialize();
  },

  methods: {
    async initialize() {
      await Promise.all([
        this.loadSyncStats(),
        this.getUsers(),
        this.getProgrammes()
      ]);
    },

    async getUsers() {
      try {
        const res = await this.$axios.get('users');
        this.users = (res.data.data || []).sort((a, b) => b.id - a.id);
      } catch (error) {
        this.showError(error.response?.data?.message || "Failed to fetch users");
      }
    },

    async getProgrammes() {
      try {
        const res = await this.$axios.get('programs');
        this.programmes = (res.data.data || []).sort((a, b) => b.id - a.id);
      } catch (error) {
        this.showError(error.response?.data?.message || "Failed to fetch programmes");
      }
    },

    async loadSyncStats() {
      try {
        this.syncing = true;
        const [usersRes, programmesRes] = await Promise.all([
          axios.get(`${env.helpdesk_base_url}/users`),
          axios.get(`${env.helpdesk_base_url}/programmes`),
        ]);

        this.syncStats.users = usersRes.data?.data?.length || 0;
        this.syncStats.institutions = 1;
        this.syncStats.programmes = programmesRes.data?.data?.length || 0;
      } catch (error) {
        console.error("Failed to load sync stats:", error);
        this.showError("Failed to load statistics");
      } finally {
        this.syncing = false;
      }
    },

    navigateToHelpdesk() {
      const authToken = localStorage.getItem("token");

      if (authToken) {
        const newWindow = window.open(
          `${env.base_url}/helpdesk?token=${authToken}`,
          "_blank"
        );
        if (newWindow) newWindow.focus();
      } else {
        this.showError("Authentication token not found. Please log in again.");
      }
    },

    confirmSync() {
      this.syncDialog = true;
    },

    async performSync() {
      this.syncDialog = false;
      this.syncing = true;
      this.syncResult = null;

      try {
        const users = this.users.map((u) => ({
          name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
          email: u.email,
          mobile: u.mobile_no || "",
        }));

        const institutions = [
          {
            code: "SAS",
            name: "SASTRA University",
            institution_type_id: 1,
          },
        ];

        const programmes = this.programmes.map((prog) => ({
          code: prog.code,
          name: prog.degree.short_name || prog.code,
        }));

        const response = await axios.post(`${env.helpdesk_base_url}/sync/all`, {
          users,
          institutions,
          programmes,
        });

        const summary = response.data.summary;
        const totalCreated =
          summary.users.created +
          summary.institutions.created +
          summary.programmes.created;

        const totalFailed =
          summary.users.failed +
          summary.institutions.failed +
          summary.programmes.failed;

        this.lastSyncSummary = {
          created: totalCreated,
          existing:
            summary.users.existing +
            summary.institutions.existing +
            summary.programmes.existing,
          failed: totalFailed,
        };

        const message = totalFailed > 0
          ? `${totalCreated} created, ${totalFailed} failed`
          : `Successfully synchronized ${totalCreated} records`;

        this.$store.commit("showSnackbar", {
          message,
          color: totalFailed > 0 ? "warning" : "success",
        });
      } catch (error) {
        console.error("Sync failed:", error);
        this.showError(
          error.response?.data?.error ||
          error.message ||
          "Sync failed. Please try again."
        );
      } finally {
        this.syncing = false;
        await this.loadSyncStats();
      }
    },

    showError(message) {
      this.$store.commit("showSnackbar", {
        message,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.disabled-state {
  opacity: 0.6;
  pointer-events: none;
}

.rounded-lg {
  border-radius: 8px !important;
}

.gap-2 {
  gap: 8px;
}

/* Compact text sizes */
.text-h6 {
  font-size: 1.125rem !important;
  line-height: 1.5 !important;
}

.text-subtitle-1 {
  font-size: 0.9375rem !important;
}

.text-subtitle-2 {
  font-size: 0.875rem !important;
}

/* Responsive */
@media (max-width: 600px) {
  .d-flex.gap-2 {
    flex-direction: column;
    width: 100%;
  }
  
  .d-flex.gap-2 > * {
    width: 100%;
  }
}
</style>